import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import claimJobEmailTemplateDeleteApi from 'models/core/claimJobEmailTemplate/delete/Api.ClaimJobEmailTemplate.Delete';
import ClaimJobEmailTemplate from 'models/core/claimJobEmailTemplate/Model.ClaimJobEmailTemplate';
import claimJobEmailTemplateSaveApi from 'models/core/claimJobEmailTemplate/save/Api.ClaimJobEmailTemplate.Save';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import { useMemo } from 'react';
import { claimJobEmailTemplateIndexRoute } from './ClaimJobEmailTemplate.Index';

export enum ClaimJobEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyClaimJobEmailTemplateAction = Utils.VerifyExtends<
  Module.Claims.ClaimJobEmailTemplate.Actions,
  Utils.ValueOf<typeof ClaimJobEmailTemplateAction>
>;
export default function useClaimJobEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Claims.ClaimJobEmailTemplate.Params,
    Module.Claims.ClaimJobEmailTemplate.Query
  >({
    route: claimJobEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ClaimJobEmailTemplate.list });
  const listApi = useApi({
    action: ClaimJobEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: claimJobEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: claimJobEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.claimJobStatus) || [];
    const claimJobStatuses = Object.values({ ...ClaimJobStatusCode })?.map((status) => status);

    return [...claimJobStatuses.filter((status: any) => !usedStatuses.includes(status)), ...[selected?.claimJobStatus]];
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [ClaimJobEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ClaimJobEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ClaimJobEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ClaimJobEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useClaimJobEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useClaimJobEmailTemplate>>();
}
